import { useMutation } from '@tanstack/react-query';
import { LoadedUser } from '../../interfaces';
import { createWaiver, invalidateWaivers } from '../../services/waivers';
import {
  CreateWaiverPayload,
  createWaiverSchema,
  UpdateWaiverResponse,
} from '../../types/waivers';
import useUser from '../global/useUser';
import { invalidateBusinessServices } from '../../services/business';

interface NewWaiverOptions {
  autoFetch?: boolean;
  onSuccess?: (waiver: UpdateWaiverResponse | null) => void | Promise<void>;
  onError?: (error: Error) => void | Promise<void>;
}

function useNewWaiver({
  onSuccess,
  onError,
  autoFetch = true,
}: NewWaiverOptions = {}) {
  const { user } = useUser<LoadedUser>();

  const businessId = user?.business?.id;

  const mutation = useMutation({
    mutationFn: async (payload: Omit<CreateWaiverPayload, 'businessId'>) => {
      if (!businessId) return null;

      const initialPayload: CreateWaiverPayload = {
        ...payload,
        businessId,
      };
      const body = await createWaiverSchema
        .noUnknown()
        .validate(initialPayload, {
          stripUnknown: true,
        });

      const response = await createWaiver(body);

      if (response && autoFetch) {
        await invalidateBusinessServices();
        await invalidateWaivers();
      }

      return response;
    },
    onSuccess,
    onError,
  });

  return {
    mutation,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export default useNewWaiver;
